import { Button, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useId, useRef, useState } from 'react';

import { apiFetch } from '../../../lib/apiFetch.js';
import { type PaymentAttempt, paymentAttemptForInvoice } from '../paymentAttempt.js';
import { invoiceQueryKey } from '../useInvoiceQuery.js';

/**
 * Add-Payment form (legacy `InvoiceDetailPage` Add-Payment card). Submits the
 * real api-host `POST /api/v1/invoices/:id/payments` as multipart form data through
 * `apiFetch` (CSRF double-submit baked in — T-21-08). On
 * success it invalidates the detail query so the totals + status badge refresh
 * from the server (Pitfall 5 — the badge reflects the latest server value).
 *
 * Every input has a visible `<label htmlFor>` (A11Y-01). One idempotency key is
 * retained across transport failures and cleared only after authoritative success
 * (T-27.2-05-02 — the backend dedupe is the backstop for a double submit).
 *
 * There is deliberately NO attachment input here: recording a payment carries no
 * document in prod, and the drawer must match that contract (27.2-05 parity).
 * Amounts are validated to the server's own shape — a positive number with at most
 * two decimals — before a request goes out, so a typo fails locally instead of as an
 * opaque 400.
 */

/** The server accepts `^\d+(\.\d{1,2})?$` and > 0; the ceiling is ours, to catch typos. */
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const MIN_PAYMENT_AMOUNT = 0.01;
const MAX_PAYMENT_AMOUNT = 1_000_000;

/** Turn a backend error code into a sentence a user can act on. */
function paymentErrorMessage(code: string | undefined, t: (key: string) => string): string {
  if (code === 'idempotency_key_required') return t('invoices:payments.errorIdempotency');
  // Domain errors from the money layer are already written for humans ("Amount must be
  // greater than 0."); anything else that looks like a bare code falls back to the generic.
  if (!code || /^[a-z0-9_]+$/.test(code)) return t('invoices:form.genericError');
  return code;
}

export function AddPaymentForm({ invoiceId }: { invoiceId: number }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dateId = useId();
  const amountId = useId();
  const methodId = useId();
  const referenceId = useId();

  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paymentAttemptRef = useRef<PaymentAttempt | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setError(null);

    const trimmedAmount = amount.trim();
    const parsedAmount = Number(trimmedAmount);
    if (
      !AMOUNT_PATTERN.test(trimmedAmount) ||
      parsedAmount < MIN_PAYMENT_AMOUNT ||
      parsedAmount > MAX_PAYMENT_AMOUNT
    ) {
      setError(
        t('invoices:payments.errorAmountRange', {
          min: MIN_PAYMENT_AMOUNT.toFixed(2),
          max: MAX_PAYMENT_AMOUNT.toLocaleString('en-US', { minimumFractionDigits: 2 }),
        }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const attempt = paymentAttemptForInvoice(paymentAttemptRef.current, invoiceId);
      paymentAttemptRef.current = attempt;
      const idempotencyKey = attempt.key;
      const formData = new FormData();
      formData.set('date', date);
      formData.set('amount', trimmedAmount);
      formData.set('method', method);
      formData.set('reference', reference);
      const response = await apiFetch(`/api/v1/invoices/${invoiceId}/payments`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: formData,
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
      } | null;
      if (!response.ok || !result || result.ok === false) {
        setError(paymentErrorMessage(result?.error, t));
        return;
      }
      paymentAttemptRef.current = null;
      setDate('');
      setAmount('');
      setMethod('');
      setReference('');
      form.reset();
      // Refetch the invoice detail so the new payment row, the totals and the status badge
      // all come back from the server rather than from an optimistic guess.
      await queryClient.invalidateQueries({ queryKey: invoiceQueryKey(invoiceId) });
    } catch {
      setError(t('invoices:form.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-md border border-border bg-card p-6">
      <h2 className="mb-4 font-semibold text-foreground text-xl">
        {t('invoices:payments.addHeading')}
      </h2>
      {error ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive text-sm"
        >
          {error}
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={dateId} className="font-medium text-foreground text-sm">
            {t('invoices:payments.date')}
          </label>
          <input
            id={dateId}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="h-11 rounded-md border border-input bg-background px-3 font-mono text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={amountId} className="font-medium text-foreground text-sm">
            {t('invoices:payments.amount')}
          </label>
          <input
            id={amountId}
            type="number"
            step="0.01"
            min={MIN_PAYMENT_AMOUNT}
            max={MAX_PAYMENT_AMOUNT}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className="h-11 rounded-md border border-input bg-background px-3 font-mono text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={methodId} className="font-medium text-foreground text-sm">
            {t('invoices:payments.method')}
          </label>
          <input
            id={methodId}
            type="text"
            maxLength={50}
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="h-11 rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={referenceId} className="font-medium text-foreground text-sm">
            {t('invoices:payments.reference')}
          </label>
          <input
            id={referenceId}
            type="text"
            maxLength={100}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="h-11 rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? t('invoices:form.saving') : t('invoices:payments.submit')}
          </Button>
        </div>
      </form>
    </section>
  );
}
